import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import AutoTableHeader from './AutoTableHeader'
import AutoTableRow from './AutoTableRow'
import AutoTableRowCollapsible from './AutoTableRowCollapsible'
import AutoTableFilters from './AutoTableFilters'
import {
  EmptyTablePrimaryMessage,
  EmptyTableRow,
  EmptyTableSecondaryMessage,
  Table,
  TableHead,
} from './component.styles'

const AutoTable = React.forwardRef(
  (
    {
      className,
      columnWidths,
      columns = [],
      dark,
      emptyDataPrimaryMessage,
      emptyDataSecondaryMessage,
      filter,
      filterData,
      handleRowClick,
      hover,
      id,
      isCollapsible,
      multiColSpanValue,
      numOfColumns,
      onToggleSort,
      resolveCellValue,
      resolveClassName,
      role,
      rowClassName,
      rows,
      size,
      sort,
      sortData,
      sortedRows,
      striped,
      updateFilter,
    },
    ref,
  ) => {
    function splitRowsByStringOrTitle(array) {
      return array.reduce((acc, curr) => {
        if (typeof curr === 'string' || curr.type === 'title') {
          acc.push([curr])
        } else {
          acc[acc.length - 1].push(curr)
        }
        return acc
      }, [])
    }
    const styles = {
      dark: dark,
      size: size,
      hover: hover,
      striped: striped,
    }
    const theadClassName = dark ? 'thead-dark' : 'thead-light'
    const enableFilter = filter && rows.length > 1
    const tableRows = isCollapsible
      ? splitRowsByStringOrTitle(sortedRows)
      : sortedRows
    const colGroupWidths3 = ['50%', '25%', '25%']
    const colGroupWidths4 = ['37%', '16%', '22%', '25%']
    /** K-8 report tables with 5–6 columns need one <col> per column or widths skew. */
    /** Narrow col 1 so “Marked Done” stays in view; must sum to 100%. */
    const colGroupWidths5 = ['18%', '18%', '24%', '23%', '17%']
    const colGroupWidths6 = ['16%', '7%', '9%', '9%', '30%', '29%']
    const colGroupWidths = (() => {
      if (
        Array.isArray(columnWidths) &&
        columnWidths.length === columns.length
      ) {
        return columnWidths
      }
      switch (columns.length) {
        case 3:
          return colGroupWidths3
        case 4:
          return colGroupWidths4
        case 5:
          return colGroupWidths5
        case 6:
          return colGroupWidths6
        default: {
          const n = columns.length
          const each = (100 / n).toFixed(4)
          return Array.from({ length: n }, () => `${each}%`)
        }
      }
    })()

    if (columns.length === 0)
      return <div color="warning">No columns specified</div>

    return (
      <Table className={className} {...styles} role={role}>
        <colgroup>
          {colGroupWidths.map((width, i) => (
            <col key={`col-${i}`} style={{ width: `${width}` }} />
          ))}
        </colgroup>
        <TableHead className={theadClassName}>
          <AutoTableHeader
            columns={columns}
            id={id}
            onToggleSort={onToggleSort}
            resolveClassName={resolveClassName}
            sort={sort && sortedRows.length > 1}
            sortData={sortData}
          />
          {enableFilter && (
            <AutoTableFilters
              columns={columns}
              filter={filterData}
              id={id}
              resolveClassName={resolveClassName}
              updateFilter={updateFilter}
            />
          )}
        </TableHead>
        <tbody>
          {sortedRows.length === 0 && (
            <EmptyTableRow colSpan={numOfColumns}>
              <EmptyTablePrimaryMessage>
                {emptyDataPrimaryMessage}
              </EmptyTablePrimaryMessage>
              <EmptyTableSecondaryMessage>
                {emptyDataSecondaryMessage}
              </EmptyTableSecondaryMessage>
            </EmptyTableRow>
          )}
          {sortedRows.length > 0 && (
            <Fragment key="auto-table-rows">
              {isCollapsible &&
                tableRows.map((rowSet, rowIndex) => (
                  <AutoTableRowCollapsible
                    colGroupWidths={colGroupWidths}
                    columns={columns}
                    key={rowIndex}
                    multiColSpanValue={multiColSpanValue}
                    resolveCellValue={resolveCellValue}
                    resolveClassName={resolveClassName}
                    rowClassName={rowClassName}
                    rowSet={rowSet}
                  />
                ))}
              {!isCollapsible &&
                tableRows.map((row, rowIndex) => (
                  <AutoTableRow
                    columns={columns}
                    dataTestId={row.dataTestId}
                    handleRowClick={handleRowClick}
                    id={id}
                    key={rowIndex}
                    multiColSpanValue={multiColSpanValue}
                    ref={ref?.current?.[rowIndex]}
                    resolveCellValue={resolveCellValue}
                    resolveClassName={resolveClassName}
                    rowClassName={rowClassName}
                    rowData={row}
                    rowIndex={rowIndex}
                  />
                ))}
            </Fragment>
          )}
        </tbody>
      </Table>
    )
  },
)

AutoTable.propTypes = {
  className: PropTypes.string,
  /** When set and length matches `columns`, overrides default colgroup widths (e.g. ['40%', '20%', '20%', '20%']). */
  columnWidths: PropTypes.arrayOf(PropTypes.string),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      fieldValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
        .isRequired,
      sort: PropTypes.bool,
      filter: PropTypes.bool,
      sortValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      filterValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      headerClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      cellClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    }),
  ).isRequired,
  dark: PropTypes.bool,
  defaultFilter: PropTypes.arrayOf(
    PropTypes.shape({
      columnTitle: PropTypes.string.isRequired,
      query: PropTypes.string.isRequired,
    }),
  ),
  defaultSort: PropTypes.shape({
    columnTitle: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }),
  emptyDataPrimaryMessage: PropTypes.string,
  emptyDataSecondaryMessage: PropTypes.string,
  filter: PropTypes.bool,
  filterData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  handleRowClick: PropTypes.func,
  hover: PropTypes.bool,
  id: PropTypes.string.isRequired,
  isCollapsible: PropTypes.bool,
  multiColSpanValue: PropTypes.number,
  numOfColumns: PropTypes.number,
  onToggleSort: PropTypes.func.isRequired,
  resolveCellValue: PropTypes.func.isRequired,
  resolveClassName: PropTypes.func.isRequired,
  role: PropTypes.string,
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  rows: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  ).isRequired,
  size: PropTypes.string,
  sort: PropTypes.bool,
  sortData: PropTypes.object,
  sortedRows: PropTypes.array,
  striped: PropTypes.bool,
  updateFilter: PropTypes.func.isRequired,
}

AutoTable.defaultProps = {
  emptyDataPrimaryMessage: 'No results found',
  emptyDataSecondaryMessage: 'There are no available data for this table',
}

export default AutoTable
